/**
 * "Include Current" is a no-op when the lookback window's upper edge ("to" / lookback.end) is not 0.
 *
 * The engine adds the current transaction to the candidate set only to filter it back out via the
 * disjoint-window upper clamp: for end != 0 the realtime upper bound is (txnDate - end), and the
 * current txn (on txnDate) is not before it, so it is dropped regardless of includeCurrentTransaction
 * (FraudRealtimeAggregationFunction.calculateRealtimeValueFromLookback). A window "30d ago -> 1d ago"
 * semantically excludes today; the flag only has an effect when end = 0 (window runs up to now).
 *
 * So `includeCurrentTransaction = true` together with a non-zero `end` is a contradictory config that
 * silently does nothing. These pure helpers find that combination in a variant record so the drawer
 * can block save until the author fixes it (set end = 0, or uncheck Include Current).
 */

/** True when a single aggregation function's args set includeCurrentTransaction with a non-zero end. */
export const isIncludeCurrentEndConflict = (args) => {
  const a = args || {};
  if (a.includeCurrentTransaction !== true) return false;
  const end = Number((a.lookback && a.lookback.end) || 0);
  return Number.isFinite(end) && end !== 0;
};

/** Inline hint shown live on the function card when the conflict is present. */
export const INCLUDE_CURRENT_END_CONFLICT_HINT =
  '"Include Current" has no effect while the lookback "To" is not 0 — the current transaction ' +
  'is outside the window and is always excluded. Set "To" to 0, or uncheck "Include Current".';

/**
 * @returns {string[]} names of aggregation functions that set includeCurrentTransaction with a
 * non-zero lookback end (empty = no violation).
 */
export const findIncludeCurrentWithNonZeroEnd = (record) =>
  ((record && record.function) || [])
    .filter((fn) => isIncludeCurrentEndConflict(fn && fn.args))
    .map((fn) => fn.name || '(unnamed)');

/** Build the save-block message for the offending function names, or null when there is none. */
export const includeCurrentWithNonZeroEndMessage = (names) => {
  if (!names || names.length === 0) return null;
  const subject =
    names.length > 1
      ? `Functions ${names.join(', ')} have`
      : `Function ${names[0]} has`;
  return (
    `${subject} "Include Current" enabled with a non-zero lookback "to". ` +
    'The current transaction falls outside that window and is always excluded, so the option ' +
    'has no effect. Set the lookback "to" to 0, or uncheck "Include Current".'
  );
};
